import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import './CheckoutPage.css';
import { getDisplayPrices } from '../../utils/priceUtils';

const CheckoutPage = () => {
  const { token, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const queryParams = new URLSearchParams(location.search);
  const courseIds = queryParams.get('courseIds')?.split(',') || [];
  const giftEmail = queryParams.get('giftEmail');

  const paymentMethods = [
    { id: 'card', label: 'Credit/Debit Card', icon: '💳', razorpayKey: 'card' },
    { id: 'netbanking', label: 'Net Banking', icon: '🏦', razorpayKey: 'netbanking' },
    { id: 'upi', label: 'UPI', icon: '📱', razorpayKey: 'upi' },
    { id: 'wallet', label: 'Wallet', icon: '💰', razorpayKey: 'wallet' },
  ];

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchCourses = async () => {
      try {
        const responses = await Promise.all(
          courseIds.map((id) =>
            axios.get(api.getCourseById(id), {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ),
        );
        setCourse(responses.map((res) => res.data.course));
      } catch (error) {
        console.error('Error fetching courses:', error);
        toast.error('Failed to load course details');
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [courseIds, token, navigate]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!course || course.length === 0) return;

    // Calculate total amount first
    const totalAmount = course.reduce(
      (sum, c) => {
        // Convert to numbers for comparison
        const offerPriceNum = Number(c.offerPrice) || 0;
        const actualPriceNum = Number(c.actualPrice) || 0;
        
        // Check if course is free based on database values
        // A course is free if: isFree flag is set, OR both prices are 0, OR offerPrice is explicitly 0
        const isCourseFree = c.isFree || 
                           offerPriceNum === 0 ||
                           (actualPriceNum === 0 && (c.offerPrice === undefined || c.offerPrice === null));
        
        if (isCourseFree) {
          return sum + 0; // Free course contributes 0 to total
        }
        
        const prices = getDisplayPrices(c);
        // Use offerPrice if it exists and is greater than 0, otherwise use actualPrice
        return sum + (prices.offerPrice !== undefined && prices.offerPrice > 0 ? prices.offerPrice : prices.actualPrice);
      },
      0,
    );

    // Check if courses are free using the same logic as backend
    // A course is free if: isFree flag is set, OR offerPrice is 0, OR both prices are 0/undefined
    // Handle both number and string values
    const isFree = course.every(c => {
      // Convert to numbers for comparison
      const offerPriceNum = Number(c.offerPrice) || 0;
      const actualPriceNum = Number(c.actualPrice) || 0;
      
      const isCourseFree = c.isFree || 
                          offerPriceNum === 0 ||
                          (actualPriceNum === 0 && (c.offerPrice === undefined || c.offerPrice === null));
      return isCourseFree;
    }) || totalAmount === 0;

    if (isFree) {
      try {
        if (giftEmail) {
          // Gifting free course: do NOT enroll sender, only add gifted record & notify recipient
          await axios.post(api.addGifted, {
            recipientEmail: giftEmail,
            courseId: course[0]._id,
            courseTitle: course[0].title,
            senderEmail: profile?.email,
          }, { headers: { Authorization: `Bearer ${token}` } });

          toast.success(`The free course has been gifted to ${giftEmail}.`);
          navigate('/gifted');
        } else {
          // Normal free course enrollment for sender
          // Only enroll courses that are actually free (match backend logic)
          // Handle both number and string values, and check calculated prices as fallback
          const freeCourses = course.filter(c => {
            // Convert to numbers for comparison (handles string "0" values)
            const offerPriceNum = Number(c.offerPrice) || 0;
            const actualPriceNum = Number(c.actualPrice) || 0;
            
            // Check database values first (matches backend logic exactly)
            const isCourseFreeByDB = c.isFree === true || 
                                    offerPriceNum === 0 ||
                                    (actualPriceNum === 0 && (c.offerPrice === undefined || c.offerPrice === null));
            
            // If not free by DB values, check calculated prices as fallback
            // This handles cases where prices might be calculated differently
            if (!isCourseFreeByDB) {
              const prices = getDisplayPrices(c);
              const calculatedOfferPrice = Number(prices.offerPrice) || 0;
              const calculatedActualPrice = Number(prices.actualPrice) || 0;
              // If both calculated prices are 0, consider it free
              if (calculatedOfferPrice === 0 && calculatedActualPrice === 0) {
                return true;
              }
            }
            
            return isCourseFreeByDB;
          });

          // If no courses match free criteria but totalAmount is 0, all courses are effectively free
          // This handles edge cases where courses show as free but don't match exact criteria
          const coursesToEnroll = freeCourses.length > 0 ? freeCourses : (totalAmount === 0 ? course : []);

          if (coursesToEnroll.length === 0) {
            // Debug logging for production issues
            console.error('Free course enrollment failed:', {
              totalAmount,
              courseCount: course.length,
              freeCoursesCount: freeCourses.length,
              courses: course.map(c => ({
                id: c._id,
                title: c.title,
                isFree: c.isFree,
                offerPrice: c.offerPrice,
                actualPrice: c.actualPrice,
                calculatedPrices: getDisplayPrices(c),
              })),
            });
            toast.error('No free courses to enroll in. Please contact support if this course should be free.');
            return;
          }

          // Debug logging
          console.log('Enrolling in free courses:', {
            coursesToEnroll: coursesToEnroll.map(c => ({ id: c._id, title: c.title })),
            totalAmount,
          });

          const responses = await Promise.all(
            coursesToEnroll.map(async (c) => {
              try {
                const response = await axios.post(
                  api.enrollFreeCourse,
                  { courseId: c._id },
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                return { success: true, data: response.data, courseId: c._id };
              } catch (error) {
                console.error(`Failed to enroll in course ${c._id}:`, {
                  error: error.response?.data || error.message,
                  status: error.response?.status,
                  courseId: c._id,
                  courseTitle: c.title,
                });
                return { success: false, error, courseId: c._id };
              }
            }),
          );

          const successful = responses.filter(res => res.success);
          const failed = responses.filter(res => !res.success);

          if (failed.length === 0 && successful.length === coursesToEnroll.length) {
            toast.success('Successfully enrolled in free course(s)!');
            navigate('/learning', { state: { course: coursesToEnroll } });
          } else if (successful.length > 0) {
            // Partial success
            toast.warning(`Enrolled in ${successful.length} course(s), but ${failed.length} failed.`);
            const successfulCourses = coursesToEnroll.filter(c => 
              successful.some(s => s.courseId === c._id)
            );
            navigate('/learning', { state: { course: successfulCourses } });
          } else {
            // All failed
            const firstError = failed[0]?.error;
            const errorMessage = firstError?.response?.data?.message || 
                                firstError?.message || 
                                'Failed to enroll in free courses. Please try again or contact support.';
            console.error('All enrollment attempts failed:', failed);
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error('Error enrolling or gifting free courses:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to process free courses. Please try again.';
        toast.error(errorMessage);
      }
      return;
    }

    // Proceed with paid course flow (existing code)
    if (totalAmount <= 0) {
      toast.error('Invalid amount. Please try again.');
      return;
    }

    setIsProcessing(true);
    try {
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        toast.error('Failed to load payment gateway. Please refresh the page and try again.');
        setIsProcessing(false);
        return;
      }

      console.log('Creating payment order:', {
        amount: totalAmount,
        courseIds,
        currency: 'INR',
        apiEndpoint: api.createOrder,
      });

      let orderResponse;
      try {
        orderResponse = await axios.post(
          api.createOrder,
          {
            amount: totalAmount,
            courseIds,
            currency: 'INR',
            method: paymentMethods.find(m => m.id === selectedMethod)?.razorpayKey,
          },
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000, // 30 second timeout
          },
        );
      } catch (orderError) {
        console.error('Error creating payment order:', {
          error: orderError.response?.data || orderError.message,
          status: orderError.response?.status,
          apiUrl: api.createOrder,
          courseIds,
          amount: totalAmount,
        });
        const errorMsg = orderError.response?.data?.message || 
                        orderError.message || 
                        'Failed to create payment order. Please try again.';
        toast.error(errorMsg);
        setIsProcessing(false);
        return;
      }

      const { orderId: razorpayOrderId, key: razorpayKey } = orderResponse.data;

      const options = {
        key: razorpayKey,
        amount: totalAmount * 100,
        currency: 'INR',
        name: 'UlsaApp',
        description: `Purchase: ${course.map(c => c.title).join(', ')}`,
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            console.log('Payment successful, verifying payment...', {
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
            });

            let verifyResponse;
            try {
              verifyResponse = await axios.post(
                api.verifyPayment,
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  courseIds,
                },
                { 
                  headers: { Authorization: `Bearer ${token}` },
                  timeout: 30000, // 30 second timeout
                },
              );
            } catch (verifyError) {
              console.error('Payment verification error:', {
                error: verifyError.response?.data || verifyError.message,
                status: verifyError.response?.status,
                apiUrl: api.verifyPayment,
              });
              toast.error(verifyError.response?.data?.message || 'Payment verification failed. Please contact support with payment ID: ' + response.razorpay_payment_id);
              return;
            }

            if (verifyResponse.data.success) {
              if (giftEmail) {
                await axios.post(api.addGifted, {
                  recipientEmail: giftEmail,
                  courseId: course[0]._id,
                  courseTitle: course[0].title,
                  senderEmail: profile?.email,
                  senderName: profile?.name,
                  giftMessage: queryParams.get('giftMessage') || '',
                }, { headers: { Authorization: `Bearer ${token}` } });

                toast.success(`The course has been gifted to ${giftEmail}.`);
                navigate('/gifted');
              } else {
                // Add purchased courses to user
                try {
                  const purchaseResponses = await Promise.all(
                    course.map((c) =>
                      axios.post(
                        api.addPurchasedCourse,
                        { courseId: c._id },
                        { 
                          headers: { Authorization: `Bearer ${token}` },
                          timeout: 30000,
                        },
                      ),
                    ),
                  );
                  
                  const allPurchased = purchaseResponses.every(res => res.data);
                  if (allPurchased) {
                    toast.success('Payment successful! You can now access your courses.');
                    navigate('/learning', { state: { course } });
                  } else {
                    console.warn('Some courses may not have been added to purchased list');
                    toast.success('Payment successful! You can now access your courses.');
                    navigate('/learning', { state: { course } });
                  }
                } catch (purchaseError) {
                  console.error('Error adding purchased courses:', {
                    error: purchaseError.response?.data || purchaseError.message,
                    status: purchaseError.response?.status,
                  });
                  // Payment was successful, but failed to add to purchased list
                  // Still navigate to learning page as payment was verified
                  toast.warning('Payment successful, but there was an issue updating your course list. Please refresh the page.');
                  navigate('/learning', { state: { course } });
                }
              }
            } else {
              console.error('Payment verification returned unsuccessful:', verifyResponse.data);
              toast.error(verifyResponse.data?.message || 'Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', {
              error: error.response?.data || error.message,
              status: error.response?.status,
              stack: error.stack,
            });
            const errorMsg = error.response?.data?.message || 
                            error.message || 
                            'Payment verification failed. Please contact support.';
            toast.error(errorMsg);
          }
        },
        prefill: {
          name: profile?.name || '',
          email: profile?.email || '',
        },
        theme: {
          color: '#4a80f0',
        },
      };

      const rzp = new window.Razorpay(options);
      
      // Add error handler for Razorpay modal
      rzp.on('payment.failed', function (response) {
        console.error('Razorpay payment failed:', response);
        toast.error(response.error?.description || 'Payment failed. Please try again.');
        setIsProcessing(false);
      });

      rzp.open();
    } catch (error) {
      console.error('Payment error:', {
        error: error.response?.data || error.message,
        status: error.response?.status,
        stack: error.stack,
        apiUrl: api.createOrder,
      });
      const errorMsg = error.response?.data?.message || 
                      error.message || 
                      'Failed to process payment. Please try again.';
      toast.error(errorMsg);
      setIsProcessing(false);
    }
  };

  // Handler to remove a course from the list
  const handleRemoveCourse = (id) => {
    const updatedCourses = course.filter((c) => c._id !== id);
    setCourse(updatedCourses);
    // Update the URL's courseIds parameter
    const newCourseIds = updatedCourses.map((c) => c._id).join(',');
    const params = new URLSearchParams(location.search);
    if (newCourseIds) {
      params.set('courseIds', newCourseIds);
    } else {
      params.delete('courseIds');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  if (loading) {
    return (
      <div className="checkout-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="spinner"
        ></motion.div>
        <p>Loading course details...</p>
      </div>
    );
  }

  if (!course || course.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="checkout-error"
      >
        <div className="error-icon">×</div>
        <h3>Courses Not Found</h3>
        <p>We couldn&apos;t find the courses you&apos;re looking for.</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/courses')}
        >
          Browse Courses
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="checkout-container"
    >
      <div className="checkout-card">
        <div className="checkout-header">
          <h2>Complete Your Purchase</h2>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="checkout-card-close-btn"
            onClick={() => navigate(-1)}
            aria-label="Close checkout"
          >
            &times;
          </motion.button>
        </div>

        <div className="course-summary">
          {course.map((c) => (
            <div key={c._id} className="course-item">
              {course.length > 1 && (
                <button
                  className="remove-course-btn"
                  onClick={() => handleRemoveCourse(c._id)}
                  aria-label="Remove course"
                >
                  &times; {/* or use an icon like <FaTimes /> if using Font Awesome / react-icons */}
                </button>
              )}
              <div className="thumbnail-container">
                <img
                  src={`${api.img + c.image}`}
                  alt={c.title}
                  className="course-thumbnail"
                />
              </div>
              <div className="course-detailss">
                <h3 className="checkout-course-hed">{c.title}</h3>
                <p className="instructor">Instructor: {c.instructor}</p>
                <p>
                  <span className="price-container">
                    {(() => {
                      const prices = getDisplayPrices(c);
                      // Check database values first for consistency
                      // A course is free if: isFree flag is set, OR offerPrice is 0, OR both prices are 0
                      const isCourseFree = c.isFree || 
                                         c.offerPrice === 0 ||
                                         (c.actualPrice === 0 && c.offerPrice === undefined);
                      return (
                        <>
                          {isCourseFree ? 'Free' : `₹${prices.offerPrice}`} {!isCourseFree && (
                            <span className="original-price">₹{prices.actualPrice}</span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>

        {giftEmail && (
          <div className="gift-info">
            <h4>Gifting this course to: <span style={{ color: '#4a80f0' }}>{giftEmail}</span></h4>
          </div>
        )}

        <div className="benefits">

          <h4>What&apos;s included:</h4>
          <ul>
            <li><span className="benefit-icon">🎓</span> Lifetime access to course materials</li>
            <li><span className="benefit-icon">🏆</span> Certificate of completion</li>
            <li><span className="benefit-icon">📥</span> Downloadable resources</li>
            <li><span className="benefit-icon">💯</span> Exercises</li>
          </ul>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePayment}
          className="proceed-to-payment-btn"
        >
          {isProcessing ? (
            <>
              <span className="spinner-btn"></span>
              Processing...
            </>
          ) : (
            (() => {
              const total = course.reduce((sum, c) => {
                // Check if course is free based on database values
                // A course is free if: isFree flag is set, OR offerPrice is 0, OR both prices are 0
                const isCourseFree = c.isFree || 
                                   c.offerPrice === 0 ||
                                   (c.actualPrice === 0 && c.offerPrice === undefined);
                
                if (isCourseFree) {
                  return sum + 0; // Free course contributes 0 to total
                }
                
                const prices = getDisplayPrices(c);
                // Use offerPrice if it exists and is greater than 0, otherwise use actualPrice
                return sum + (prices.offerPrice !== undefined && prices.offerPrice > 0 ? prices.offerPrice : prices.actualPrice);
              }, 0);
              return (course.every(c => c.isFree) || total === 0) 
                ? (course.length > 1 ? 'Get these Courses' : 'Get this Course')
                : `Pay ₹${total}`;
            })()
          )}
        </motion.button>

        <div className="payment-security">
          <div className="secure-badge">
            <span className="lock-icon">🔒</span>
            <span>Secure Payment</span>
          </div>
          <p>Your payment information is processed securely. We do not store your details.</p>
          <div className="trust-badges">
            <div className="trust-badge">SSL Secured</div>
            <div className="trust-badge">PCI DSS Compliant</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CheckoutPage;